import { registerRoute } from 'workbox-routing';
import { NetworkFirst, StaleWhileRevalidate } from 'workbox-strategies';
import { CacheFirstBeforeExpiryDate } from '../strategies/CacheFirstBeforeExpiryDate';
import { QRCodesPreloadCacheStrategy } from '../strategies/QRCodesPreloadCacheStrategy';
import { NetworkFirstWithDownloadTimeout } from '../strategies/NetworkFirstWithDownloadTimeout';
import { endsWith } from 'lodash';


// Constants for cache strategies
const CACHE_FIRST_BEFORE_EXPIRY_DATE = 'CacheFirstBeforeExpiryDate';
const STALE_WHILE_REVALIDATE = 'StaleWhileRevalidate';
const NETWORK_FIRST = 'NetworkFirst';
const NETWORK_FIRST_DOWNLOAD_TIMEOUT = 'NetworkFirstDownloadTimeout';
const NETWORK_FIRST_HIGH_DOWNLOAD_TIMEOUT = 'NetworkFirstHighDownloadTimeout';

const QRCODES_PRELOAD_CACHE_STRATEGY = 'QRCodesPreloadCacheStrategy';

const matchesAny = (url, patterns) => patterns.some(substring => url.includes(substring));

//Match urls with associated cache strategy
function shouldUseCacheStrategy(getUrl, request, cacheStrategy) {
    if (getUrl.host.endsWith(DOMAIN_NAME)) {
        // Check if specific strategy is requested in the header
        if (request.headers.get('X-Use-Strategy')) {
            return request.headers.get('X-Use-Strategy') === cacheStrategy;
        }

        // Request URL matching rules for each cache strategy
        const requestsWithCacheFirstBeforeExpiryDateStrategy = [
            '/workspace/?hardware_id=',
            '/devices/get-device-by-hardware-id/'
        ];

        // Request URL matching rules for each cache strategy
        const requestsWithNetworkFirstWithMinimumTimeout = [
          '/workspace/settings/',
          '/workspace/full-settings/',
        ];

        const usersDetailRegex = /^.+\/users\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/?$/i;
        if (getUrl.href.endsWith('.js') || usersDetailRegex.test(getUrl.href)){
          //all js files get minimum timeout
          requestsWithNetworkFirstWithMinimumTimeout.push(getUrl.href)
        }

        // TODO spécifique client novo-nordisk + societe-genrales car la requête devices est trop longue
        const isWorkspacesWithDevicesStaleStrategy = getUrl.pathname === '/devices/' && (
            getUrl.href.includes('novo-nordisk') || getUrl.href.includes('societe-generale')
        );
        // TODO spécifique client cgi car la requête users est trop longue
        const isWorkspacesWithUsersStaleStrategy = getUrl.pathname === '/users/' && getUrl.href.includes('cgi');

        const isRequestWithStaleWhileRevalidateStrategy = isWorkspacesWithDevicesStaleStrategy
            || isWorkspacesWithUsersStaleStrategy || matchesAny(getUrl.href, ['/workspace/settings']);

        const requestsWithQRCodePreloadCacheStrategy = [
            '/preload_qrcodes_cache'
        ];

        // Request URL matching rules for each cache strategy
        const isWorkspacesWithNetworkFirst= matchesAny(getUrl.href, ['/users/']) &&
        ['puissance5', 'spie-immo', 'cgi', 'fnacdarty', 'prevoir', 'bredinprat', 'diot-siaci', 'scc', 'sercel-carquefou'].some(item => getUrl.href.includes(item));

        console.log("cacheStrategy", cacheStrategy)
        switch (cacheStrategy) {
            case QRCODES_PRELOAD_CACHE_STRATEGY:
                console.log(`[service-worker] cacheStrategy : ${getUrl} -> QRCODES_PRELOAD_CACHE_STRATEGY ${matchesAny(getUrl.href, requestsWithQRCodePreloadCacheStrategy)}`);
                return matchesAny(getUrl.href, requestsWithQRCodePreloadCacheStrategy);

            case STALE_WHILE_REVALIDATE:
                console.log(`[service-worker] cacheStrategy : ${getUrl} -> STALE_WHILE_REVALIDATE ${isRequestWithStaleWhileRevalidateStrategy
                  && !matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy)}`);
                return isRequestWithStaleWhileRevalidateStrategy
                    && !matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy);

            case CACHE_FIRST_BEFORE_EXPIRY_DATE:
                console.log(`[service-worker] cacheStrategy : ${getUrl} -> CACHE_FIRST_BEFORE_EXPIRY_DATE ${matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy)}`);
                return matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy);

            case NETWORK_FIRST_DOWNLOAD_TIMEOUT:
              console.log(`[service-worker] cacheStrategy : ${getUrl} -> NETWORK_FIRST_DOWNLOAD_TIMEOUT ${matchesAny(getUrl.href, requestsWithNetworkFirstWithMinimumTimeout)}`);
              return matchesAny(getUrl.href, requestsWithNetworkFirstWithMinimumTimeout);

            case NETWORK_FIRST:
              console.log(`[service-worker] cacheStrategy : ${getUrl} -> NETWORK_FIRST ${isWorkspacesWithNetworkFirst}`);
              return isWorkspacesWithNetworkFirst;

            case NETWORK_FIRST_HIGH_DOWNLOAD_TIMEOUT:
                console.log(`[service-worker] cacheStrategy : ${getUrl} -> NETWORK_FIRST_HIGH_DOWNLOAD_TIMEOUT ${!matchesAny(getUrl.href, requestsWithQRCodePreloadCacheStrategy)
                  && !isRequestWithStaleWhileRevalidateStrategy
                  && !matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy)
                  && !matchesAny(getUrl.href, requestsWithNetworkFirstWithMinimumTimeout)
                  && !isWorkspacesWithNetworkFirst
                }`);


                return !matchesAny(getUrl.href, requestsWithQRCodePreloadCacheStrategy)
                    && !isRequestWithStaleWhileRevalidateStrategy
                    && !matchesAny(getUrl.href, requestsWithCacheFirstBeforeExpiryDateStrategy)
                    && !matchesAny(getUrl.href, requestsWithNetworkFirstWithMinimumTimeout)
                    && !isWorkspacesWithNetworkFirst

            default:
                console.log(`[service-worker] cacheStrategy : ${getUrl} -> ${cacheStrategy} ${false}`);
                return false;
        }
    }
    return false;
}

// Fonction pour générer une clé de cache personnalisée pour les requêtes POST
function generateCacheKey(request) {
    const url = new URL(request.url);
    url.searchParams.sort();
    return `${url.pathname}?${url.searchParams.toString()}`;
}

// Stratégie personnalisée pour les requêtes POST spécifiques
async function customPostStrategy({ request, event }) {
    console.log(`[service-worker] customPostStrategy`)
    const cache = await caches.open('logged-in-cached-data');
    const cacheKey = generateCacheKey(request);

    // Durée maximale en millisecondes à attendre pour la réponse réseau
    const TIMEOUT = 2000; // 2 secondes

    let networkResponse;

    try {
      networkResponse = await Promise.race([
        fetch(request.clone()),
        new Promise((resolve) => setTimeout(() => resolve(null), TIMEOUT))
      ]);
    } catch (error) {
      console.log(`[service-worker] customPostStrategy failed`)
      console.log(error)

      // En cas d'erreur lors du fetch, on considère que le délai est dépassé
      networkResponse = null;
    }

    if (!networkResponse) {
      console.log(`[service-worker] customPostStrategy network post failed`)

      // Timeout ou échec du réseau : renvoyer la réponse en cache si disponible
      const cachedResponse = await cache.match(cacheKey);
      if (cachedResponse) {
        console.log(`[service-worker] customPostStrategy : Already have a cached response`)

        // En arrière-plan, tenter de mettre à jour le cache
        event.waitUntil(
          (async () => {
            console.log(`[service-worker] customPostStrategy : trying to update cache with a new network request`)
            try {
              const freshResponse = await fetch(request.clone());
              if (freshResponse && (freshResponse.status === 200 || freshResponse.status === 201)) {
                console.log(`[service-worker] customPostStrategy : got update with a new network request, updating cache`)

                const responseClone = freshResponse.clone();
                const responseBody = await responseClone.blob();
                await cache.put(cacheKey, new Response(responseBody, {
                  status: freshResponse.status,
                  statusText: freshResponse.statusText,
                  headers: freshResponse.headers
                }));
                console.log(`[service-worker] customPostStrategy : got update with a new network request, cache updated !`)

              }
            } catch (error) {
              console.log(`[service-worker] customPostStrategy : failed to update cache with a new network request`)
            }
          })()
        );
        return cachedResponse;
      } else {
        console.log(`[service-worker] customPostStrategy : No cached response, trying to refetch`)
        // Pas de réponse en cache, on renvoie directement le fetch (ce qui échouera probablement)
        return fetch(request);
      }
    } else {
      console.log(`[service-worker] customPostStrategy network post success. Cache update ...`)
      console.log('une réponse réseau a été recue, mise à jour du cache et renvoi de la réponse')
      // Réponse réseau obtenue dans le délai imparti : mise à jour du cache et renvoi de la réponse
      if (networkResponse && (networkResponse.status === 200 || networkResponse.status === 201)) {
        const responseClone = networkResponse.clone();
        const responseBody = await responseClone.blob();
        await cache.put(cacheKey, new Response(responseBody, {
          status: networkResponse.status,
          statusText: networkResponse.statusText,
          headers: networkResponse.headers
        }));
      }
      console.log(`[service-worker] customPostStrategy network post success. Cache updated !`)

      return networkResponse;
    }
}

export const setupRequestsCaching = () => {
    console.log(`[service-worker] setupRequestsCaching : registering all routes with their strategies`)

    registerRoute(
        ({ url, request }) => request.method === 'POST' && (url.pathname.includes('auth/token/refresh') ||
         url.pathname.includes('generate-hubspot-token') || //hubspot
         url.pathname.includes('generate-hmac-chatbase') || //chatbase
         url.pathname.includes('dashboards/get_tickets_dashboards')),
         customPostStrategy,
        'POST'
    );

    // Network first for long requests not included in other types
    registerRoute(
      ({ url, request }) => shouldUseCacheStrategy(url, request, NETWORK_FIRST),
      new NetworkFirst({
          cacheName: 'logged-in-cached-data',
          statuses: [200, 201, 202, 203, 204, 205, 206, 207, 208, 226],
      }),
      'GET'
    );

    // Network first for every request not included in other types
    registerRoute(
      ({ url, request }) => shouldUseCacheStrategy(url, request, NETWORK_FIRST_HIGH_DOWNLOAD_TIMEOUT),
      new NetworkFirstWithDownloadTimeout({
          cacheName: 'logged-in-cached-data',
          statuses: [200, 201, 202, 203, 204, 205, 206, 207, 208, 226],
          downloadTimeout: 10000
      }),
      'GET'
    );

    // Network first, 2s timeout (quick cache response)
    registerRoute(
      ({ url, request }) => shouldUseCacheStrategy(url, request, NETWORK_FIRST_DOWNLOAD_TIMEOUT),
      new NetworkFirstWithDownloadTimeout({
          cacheName: 'logged-in-cached-data',
          statuses: [200, 201, 202, 203, 204, 205, 206, 207, 208, 226],
          downloadTimeout: 2000
      }),
      'GET'
    );

    //TODO fc
    registerRoute(
        ({ url, request }) => shouldUseCacheStrategy(url, request, QRCODES_PRELOAD_CACHE_STRATEGY),
        new QRCodesPreloadCacheStrategy({
            cacheName: 'logged-in-cached-data'
        }),
        'GET'
    );

    //TODO fc
    registerRoute(
        ({ url, request }) => shouldUseCacheStrategy(url, request, CACHE_FIRST_BEFORE_EXPIRY_DATE),
        new CacheFirstBeforeExpiryDate({
            cacheName: 'logged-in-cached-data'
        }),
        'GET'
    );

    //TODO fc
    registerRoute(
        ({ url, request }) => shouldUseCacheStrategy(url, request, STALE_WHILE_REVALIDATE),
        new StaleWhileRevalidate({
            cacheName: 'logged-in-cached-data',
            statuses: [200, 201, 202, 203, 204, 205, 206, 207, 208, 226]
        }),
        'GET'
    );

    console.log(`[service-worker] setupRequestsCaching : registered all routes with their strategies !`)

};
